<template>
  <div class="section-group">
    <div class="availability-heading">
      <SectionTitle>Quantity By Location</SectionTitle>
      <label class="zero-stock-toggle">
        <input v-model="showZeroStockLots" type="checkbox" />
        <span>Show packages/LOT with no stock</span>
      </label>
    </div>
    <CustomTable>
      <template #head>
        <tr>
          <th>Location</th>
          <th>Stock Quantity</th>
          <th>On Order</th>
          <th>Transfer In</th>
          <th>Transfer Out</th>
        </tr>
      </template>
      <template #body>
        <tr v-for="warehouse of warehouses" :key="warehouse.id">
          <td>
            <i class="fas fa-map-marker"></i>
            {{ warehouse.name }}
          </td>
          <td>
            <div
              class="lot-breakdown"
              @mouseenter="openLotBreakdown($event, warehouse.id)"
              @mouseleave="closeLotBreakdown(warehouse.id)"
            >
              <button
                type="button"
                class="stock-quantity"
                :aria-label="`${getStockQty(warehouse.id, selectedProduct.item_code, 'stock')} units at ${warehouse.name}. Show LOT details.`"
                :aria-expanded="activeWarehouseId === warehouse.id ? 'true' : 'false'"
                @click="togglePinnedLotBreakdown($event, warehouse.id)"
                @focus="openLotBreakdown($event, warehouse.id)"
                @blur="handleTriggerBlur(warehouse.id)"
              >
                {{ getStockQty(warehouse.id, selectedProduct.item_code, "stock") }}
                <i class="material-icons" aria-hidden="true">inventory_2</i>
              </button>
              <div
                v-if="activeWarehouseId === warehouse.id"
                class="lot-breakdown-tooltip"
                :style="tooltipStyle"
                role="tooltip"
              >
                <div class="tooltip-heading">
                  <strong>{{ warehouse.name }}</strong>
                  <span>{{ visibleLots(warehouse.id).length }} LOT{{ visibleLots(warehouse.id).length === 1 ? "" : "s" }}</span>
                </div>
                <div v-if="!visibleLots(warehouse.id).length" class="tooltip-empty">
                  No LOT details available
                </div>
                <div v-else class="lot-list">
                  <div
                    v-for="lot in visibleLots(warehouse.id)"
                    :key="lot.batchNo"
                    class="lot-row"
                  >
                    <span class="lot-number">LOT {{ lot.batchNo }}</span>
                    <span>{{ lot.availableQty }} available</span>
                    <span v-if="lot.backRoomQty">{{ lot.backRoomQty }} back room</span>
                  </div>
                </div>
              </div>
            </div>
          </td>
          <td>{{ getStockQty(warehouse.id, selectedProduct.item_code, "onOrder") }}</td>
          <td>{{ getStockQty(warehouse.id, selectedProduct.item_code, "transferIn") }}</td>
          <td>{{ getStockQty(warehouse.id, selectedProduct.item_code, "transferOut") }}</td>
        </tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import CustomTable from "../layout/CustomTable.vue"
import SectionTitle from "../UI/SectionTitle.vue"

export default {
  components: { CustomTable, SectionTitle },
  data() {
    return {
      showZeroStockLots: false,
      activeWarehouseId: null,
      pinnedWarehouseId: null,
      tooltipStyle: {},
    }
  },
  props: {
    warehouses: {
      type: Array,
      default: () => [],
    },
    selectedProduct: {
      type: Object,
      required: true,
    },
    stockData: {
      type: Object,
      default: null,
    },
  },
  methods: {
    getStockQty(warehouseId, itemCode, type) {
      if (!this.stockData || !this.stockData[itemCode]) {
        return 0
      }
      const data = this.stockData[itemCode].warehouses[warehouseId]
      return (data && data[type]) || 0
    },
    lotsForWarehouse(warehouseId) {
      const itemStock = this.stockData && this.stockData[this.selectedProduct.item_code]
      const entries = (itemStock && itemStock.entries) || []
      const groupedLots = {}

      entries
        .filter((entry) => String(entry.warehouse) === String(warehouseId))
        .forEach((entry) => {
          const batchNo = entry.batch_no || "Unassigned"
          if (!groupedLots[batchNo]) {
            groupedLots[batchNo] = {
              batchNo,
              availableQty: 0,
              backRoomQty: 0,
            }
          }
          groupedLots[batchNo].availableQty += Number(entry.stock_value || 0)
          groupedLots[batchNo].backRoomQty += Number(entry.stock_value_back_room || 0)
        })

      return Object.values(groupedLots).sort((first, second) =>
        String(first.batchNo).localeCompare(String(second.batchNo), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
    },
    visibleLots(warehouseId) {
      const lots = this.lotsForWarehouse(warehouseId)
      if (this.showZeroStockLots) return lots
      return lots.filter((lot) => lot.availableQty > 0)
    },
    openLotBreakdown(event, warehouseId) {
      this.activeWarehouseId = warehouseId
      const rect = event.currentTarget.getBoundingClientRect()
      const tooltipWidth = 340
      const tooltipMaxHeight = 280
      const horizontalPadding = 12
      const left = Math.min(
        Math.max(horizontalPadding, rect.left),
        window.innerWidth - tooltipWidth - horizontalPadding
      )
      const spaceBelow = window.innerHeight - rect.bottom - horizontalPadding
      const top =
        spaceBelow >= tooltipMaxHeight
          ? rect.bottom + 8
          : Math.max(horizontalPadding, rect.top - tooltipMaxHeight - 8)
      this.tooltipStyle = {
        left: `${left}px`,
        top: `${top}px`,
      }
    },
    closeLotBreakdown(warehouseId) {
      if (this.pinnedWarehouseId !== warehouseId) {
        this.activeWarehouseId = null
      }
    },
    togglePinnedLotBreakdown(event, warehouseId) {
      if (this.pinnedWarehouseId === warehouseId) {
        this.pinnedWarehouseId = null
        this.activeWarehouseId = null
        return
      }
      this.pinnedWarehouseId = warehouseId
      this.openLotBreakdown(event, warehouseId)
    },
    handleTriggerBlur(warehouseId) {
      if (this.pinnedWarehouseId !== warehouseId) {
        this.activeWarehouseId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.availability-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.zero-stock-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  color: $textColor;
  font-size: 13px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: $primaryColor;
  }
}

.lot-breakdown {
  display: inline-flex;
}

.stock-quantity {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px;
  border: 0;
  border-bottom: 1px dashed currentColor;
  background: transparent;
  color: $primaryColor;
  font: inherit;
  cursor: pointer;

  &:focus {
    outline: 2px solid rgba($primaryColor, 0.24);
    outline-offset: 2px;
  }

  .material-icons {
    font-size: 14px;
    opacity: 0.7;
  }
}

.lot-breakdown-tooltip {
  position: fixed;
  z-index: 9999;
  width: 340px;
  max-width: calc(100vw - 24px);
  max-height: 280px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: white;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
  color: $textColor;
  font-size: 12px;
  line-height: 1.4;
  text-align: left;
}

.tooltip-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 7px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.lot-list {
  display: flex;
  flex-direction: column;
}

.lot-row {
  display: grid;
  grid-template-columns: minmax(110px, 1fr) auto auto;
  gap: 8px;
  padding: 7px 0;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.lot-number {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.tooltip-empty {
  padding-top: 8px;
  color: rgba($textColor, 0.7);
}

:deep(.dark) {
  .zero-stock-toggle {
    color: $darkTextColor;
  }

  .stock-quantity {
    color: $secondaryColorDark;
  }

  .lot-breakdown-tooltip {
    border-color: $darkBorderColor;
    background: $darkCardBg;
    color: $darkTextColor;
  }
}

@media (max-width: 768px) {
  .availability-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .lot-row {
    grid-template-columns: 1fr auto;

    span:last-child:nth-child(3) {
      grid-column: 1 / -1;
    }
  }
}
</style>
